/**
 * iter845q — Audit-log admin viewer.
 *
 * Route: /settings/audit-log
 * Requires: admin or manager role
 *
 * Shows a filterable table of every write mutation on /api/*.
 * Backed by GET /api/admin/audit-log and /api/admin/audit-log/stats.
 */
import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Shield, RefreshCw, Search, Activity, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const METHOD_COLORS = {
  POST: 'bg-emerald-600', PUT: 'bg-amber-600', PATCH: 'bg-sky-600', DELETE: 'bg-rose-600',
};

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ method: '', path_contains: '', hours: 24 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.method) params.method = filters.method;
      if (filters.path_contains) params.path_contains = filters.path_contains;
      const [listRes, statsRes] = await Promise.all([
        api.get('/admin/audit-log', { params }),
        api.get('/admin/audit-log/stats', { params: { hours: filters.hours } }),
      ]);
      setRows(listRes.data?.items || []);
      setTotal(listRes.data?.total || 0);
      setStats(statsRes.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="audit-log-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center">
            <Shield size={18} />
          </div>
          <div>
            <h1 className="text-xl font-bold">Audit Log</h1>
            <p className="text-xs text-muted-foreground">Every create / update / delete action across your workspace, last 30 days.</p>
          </div>
        </div>

        {/* Stats tiles */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Events / {stats.hours}h</div>
              <div className="text-2xl font-bold mt-1 tabular-nums" data-testid="stat-total">{stats.total_events}</div>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Errors (4xx/5xx)</div>
              <div className="text-2xl font-bold mt-1 tabular-nums text-rose-600" data-testid="stat-errors">
                {(stats.top || []).reduce((s, r) => s + (r.errors || 0), 0)}
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Active users</div>
              <div className="text-2xl font-bold mt-1 tabular-nums" data-testid="stat-users">
                {new Set((stats.top || []).map((r) => r.user_email)).size}
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Avg latency</div>
              <div className="text-2xl font-bold mt-1 tabular-nums">
                {(stats.top || []).length ? Math.round((stats.top || []).reduce((s, r) => s + (r.avg_ms || 0), 0) / stats.top.length) : 0}<span className="text-xs font-normal ml-0.5">ms</span>
              </div>
            </CardContent></Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={filters.method || 'all'} onValueChange={(v) => setFilters({ ...filters, method: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-32" data-testid="filter-method"><SelectValue placeholder="Method" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All methods</SelectItem>
              <SelectItem value="POST">POST</SelectItem>
              <SelectItem value="PUT">PUT</SelectItem>
              <SelectItem value="PATCH">PATCH</SelectItem>
              <SelectItem value="DELETE">DELETE</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-7 h-9 w-64"
              placeholder="Path contains…"
              value={filters.path_contains}
              onChange={(e) => setFilters({ ...filters, path_contains: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              data-testid="filter-path"
            />
          </div>
          <Select value={String(filters.hours)} onValueChange={(v) => setFilters({ ...filters, hours: Number(v) })}>
            <SelectTrigger className="w-32" data-testid="filter-hours"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 1h</SelectItem>
              <SelectItem value="24">Last 24h</SelectItem>
              <SelectItem value="168">Last 7d</SelectItem>
              <SelectItem value="720">Last 30d</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="btn-refresh">
            <RefreshCw size={13} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <div className="ml-auto text-xs text-muted-foreground" data-testid="row-count">{rows.length} / {total.toLocaleString()} rows</div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="audit-table">
                <thead className="bg-stone-100 dark:bg-stone-900/60 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold">Timestamp</th>
                    <th className="text-left px-3 py-2 font-bold">User</th>
                    <th className="text-left px-3 py-2 font-bold">Method</th>
                    <th className="text-left px-3 py-2 font-bold">Path</th>
                    <th className="text-right px-3 py-2 font-bold">Status</th>
                    <th className="text-right px-3 py-2 font-bold">Time</th>
                    <th className="text-left px-3 py-2 font-bold">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/40" data-testid={`row-${r.id}`}>
                      <td className="px-3 py-2 tabular-nums">{new Date(r.ts).toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold">{r.user_email || <span className="text-stone-400">anonymous</span>}</div>
                        {r.user_role && <div className="text-[10px] text-muted-foreground uppercase">{r.user_role}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`${METHOD_COLORS[r.method] || 'bg-stone-500'} text-white hover:bg-opacity-90 text-[10px]`}>{r.method}</Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px]">{r.path}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <span className={r.status >= 400 ? 'text-rose-600 font-bold' : r.status >= 300 ? 'text-amber-600' : 'text-emerald-600'}>{r.status}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.duration_ms}<span className="text-[10px] text-muted-foreground ml-0.5">ms</span></td>
                      <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{r.ip}</td>
                    </tr>
                  ))}
                  {!rows.length && !loading && (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">
                      <Activity size={18} className="inline mr-1" /> No audit events yet — mutations will show up here as soon as they happen.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Top-actors leaderboard */}
        {stats?.top?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5"><AlertTriangle size={13} /> Top actors (last {filters.hours}h)</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {stats.top.slice(0, 8).map((r, i) => (
                  <div key={i} className="flex items-center justify-between border rounded-lg px-3 py-2 text-xs">
                    <div>
                      <div className="font-semibold">{r.user_email}</div>
                      <div className="text-[10px] text-muted-foreground uppercase">{r.method} · avg {r.avg_ms}ms</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold tabular-nums">{r.count}</div>
                      {r.errors > 0 && <div className="text-[10px] text-rose-600">{r.errors} errors</div>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
